import React, { useEffect, useState } from 'react';
import { 
  useGlobalActiveGeography, 
  useGlobalSelectedId,
  onSetActiveGeography,
  onSetSelectedId,
  onSetActivePage,
  onSetDBModuleId,
  onSetModuleId,
  onSetActiveModuleId,
  onSetActiveIndicator,
  onSetBivariateIndicator,
  onSetActivePointStatus,
} from '../../data/StatusStore';
import { decodeString } from '../utilities/Utilities';
import { processSparklineTags } from './InlineSparkline';
import './WideBlurb.scss';

export interface IFrameBlurbProps {
  options: any;
  data: any;
  width: number;
  height: number;
  colorClass: string;
  setShowBackToStories: Function | null;
}

const parseCustomMarkup = (input: string): string => {
  let pos = 0;

  function parseUntil(stopTag?: string): string {
    let result = '';

    while (pos < input.length) {
      if (input[pos] === '[') {
        const end = input.indexOf(']', pos);
        if (end === -1) {
          result += input.slice(pos);
          pos = input.length;
          break;
        }
        let token = input.slice(pos + 1, end).trim();
        if (stopTag) {
          const lowerToken = token.toLowerCase();
          if (
            lowerToken === '/' + stopTag.toLowerCase() ||
            lowerToken === stopTag.toLowerCase() + '/'
          ) {
            pos = end + 1;
            return result;
          }
        }
        pos = end + 1;
        if (token.endsWith('/')) {
          token = token.slice(0, -1).trim();
        }
        const tagName = token;
        const inner = parseUntil(tagName);
        result += `<${tagName.toLowerCase()} class="custom-${tagName.toLowerCase()}">` + inner + `</${tagName.toLowerCase()}>`;
      } else {
        result += input[pos];
        pos++;
      }
    }
    return result;
  }

  return `<span class="custom-text-plain">` + parseUntil() + `</span>`;
};

const IFrameBlurb: React.FC<IFrameBlurbProps> = ({ options, data, width, height, colorClass, setShowBackToStories }) => {
  const contentTemplate = options.variables[0].sort["Stories Content"] || "";
  const variableName = options.variables[0].variable;
  const activeGeography = useGlobalActiveGeography();
  const selectedId = useGlobalSelectedId();
  const [htmlContent, setHtmlContent] = useState<string>(() => parseCustomMarkup(contentTemplate));

  // The iframe URL comes from "Stories Asset"
  const iframeSrc = options.variables[0].sort["Stories Asset"] || "";

  const handleButtonClick = () => {
    if (!iframeSrc) return;
    const params = new URLSearchParams(iframeSrc.split('?')[1] || '');
    const page = params.get('page');
    const geo = params.get('activeGeography');
    const selectedIdParam = params.get('selectedId');

    // Phase 1 (immediate): dispatch all params that are safe to set while still on the
    // stories page (excludes page, activeGeography, and selectedId).
    params.forEach((value, key) => {
      switch (key) {
        case 'moduleId': onSetModuleId(value); break;
        case 'activeModuleId': onSetActiveModuleId(value); break;
        case 'DBModuleId': onSetDBModuleId(value); break;
        case 'activeIndicator': onSetActiveIndicator(decodeString(value), options.type?.toLowerCase(), options.variables[0].sort.moduleId); break;
        case 'bivariateIndicator': onSetBivariateIndicator(decodeString(value), options.type?.toLowerCase(), options.variables[0].sort.moduleId); break;
        case 'activePointStatus': onSetActivePointStatus(`activePointStatus=${value}`); break;
        default: break;
      }
    });

    // Phase 2 (+250ms): navigate away from stories first.
    // Geography MUST NOT change while story charts are still visible — they lack
    // Tract data and will crash on re-render.
    setTimeout(() => {
      if (page) {
        setShowBackToStories && setShowBackToStories(true);
        onSetActivePage(page);
      }

      // Phase 3 (next macrotask): change geography only after the stories page has unmounted.
      // onSetActivePage above is debounced(0ms) — it schedules its macrotask first.
      // This setTimeout(0) queues behind it, so the page flip (stories → explorer) commits
      // before activeGeography changes. Then override selectedId immediately after, because
      // setActiveGeography() internally calls setSelectedId(cachedSelectedIds[geography])
      // and would overwrite it otherwise.
      setTimeout(() => {
        if (geo) onSetActiveGeography(geo);
        if (selectedIdParam) onSetSelectedId(selectedIdParam);
      }, 0);
    }, 250);
  };

  const updateContent = () => {
    if (selectedId) {
      setHtmlContent(processSparklineTags(contentTemplate, parseCustomMarkup, data?.dataArray?.[variableName], colorClass, variableName));
    } else {
      setHtmlContent(processSparklineTags(contentTemplate, parseCustomMarkup, data?.dataArray?.[variableName], colorClass, variableName));
    }
  };

  useEffect(() => {
    updateContent();
  }, [activeGeography, selectedId]);

  useEffect(() => {
    updateContent();
  }, []);

  return (
    <div 
      style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        boxSizing: 'border-box',
        position: 'relative'
      }}
    >
      {/* TEXT SECTION */}
      <div
        style={{
          width: '100%',
          lineHeight: '12pt',
          overflowY: 'auto',
          padding: '16px 0',
          maxWidth: '600px',
        }}
      >
        <div dangerouslySetInnerHTML={{ __html: htmlContent }} />
      </div>

      {/* LINK BUTTON */}
      {iframeSrc && (
        <div style={{ padding: '4px 0 12px 0' }}>
          <button
            onClick={handleButtonClick}
            style={{
              display: 'inline-block',
              padding: '6px 16px',
              fontSize: '13px',
              fontWeight: 600,
              color: '#fff',
              backgroundColor: '#555',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
            }}
          >
            Take me to this view...
          </button>
        </div>
      )}

      {/* IFRAME SECTION — render at 2x size then scale to 50% so desktop layout shows */}
      {iframeSrc && (
        <div 
          style={{
            width: '100%',
            height: '500px',
            overflow: 'hidden',
            position: 'relative',
          }}
        >
          <iframe
            src={iframeSrc}
            title="Live Preview"
            style={{
              width: '200%',
              height: '1000px',
              border: '1px solid #e0e0e0',
              borderRadius: '4px',
              transform: 'scale(0.5)',
              transformOrigin: 'top left',
            }}
            sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
          />
        </div>
      )}
    </div>
  );
};

export default IFrameBlurb;
